import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { opsApi, type RangePreset, type ReportEntry } from '../api/ops.api';

export function useDashboardStats(params?: { preset?: RangePreset; from?: string; to?: string }) {
  return useQuery({
    queryKey: ['ops', 'stats', params],
    queryFn: () => opsApi.stats(params),
    refetchInterval: 30_000,
  });
}

export function useAuditLogs(params: any) {
  return useQuery({
    queryKey: ['ops', 'audit-logs', params],
    queryFn: () => opsApi.auditLogs(params),
  });
}

export function useAuditActions() {
  return useQuery({
    queryKey: ['ops', 'audit-actions'],
    queryFn: () => opsApi.auditActions(),
    staleTime: Infinity,
  });
}

export function useReports(params: any) {
  return useQuery({
    queryKey: ['ops', 'reports', params],
    queryFn: () => opsApi.reports(params),
  });
}

export function useReportAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, adminNotes }: { id: number; status: ReportEntry['status']; adminNotes?: string }) =>
      opsApi.reportAction(id, status, adminNotes),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ops', 'reports'] });
      qc.invalidateQueries({ queryKey: ['ops', 'stats'] });
      toast.success('Report updated');
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to update report'),
  });
}

export function useUserSuspend() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, reason }: { id: number; status: 'ACTIVE' | 'SUSPENDED' | 'UNDER_REVIEW'; reason?: string }) =>
      opsApi.suspendUser(id, status, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['ops', 'stats'] });
      toast.success('User status updated');
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to update user status'),
  });
}

export function useBulkUserStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userIds, status, reason }: { userIds: number[]; status: 'ACTIVE' | 'SUSPENDED' | 'UNDER_REVIEW'; reason?: string }) =>
      opsApi.bulkUserStatus(userIds, status, reason),
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['ops', 'stats'] });
      toast.success(`Updated ${data?.affected ?? 0} users`);
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Bulk update failed'),
  });
}

export function useBulkAdStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ adIds, status, rejectReason }: { adIds: number[]; status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'REJECTED'; rejectReason?: string }) =>
      opsApi.bulkAdStatus(adIds, status, rejectReason),
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['roomAds'] });
      qc.invalidateQueries({ queryKey: ['ops', 'stats'] });
      toast.success(`Updated ${data?.affected ?? 0} ads`);
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Bulk update failed'),
  });
}

export function useBulkAdDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (adIds: number[]) => opsApi.bulkAdDelete(adIds),
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['roomAds'] });
      qc.invalidateQueries({ queryKey: ['ops', 'stats'] });
      toast.success(`Deleted ${data?.affected ?? 0} ads`);
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Bulk delete failed'),
  });
}

export function useBulkAdFlags() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ adIds, flags }: { adIds: number[]; flags: Record<string, boolean | undefined> }) =>
      opsApi.bulkAdFlags(adIds, flags),
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['roomAds'] });
      toast.success(`Updated ${data?.affected ?? 0} ads`);
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Bulk flags update failed'),
  });
}

export function useBulkWantedStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ adIds, status, rejectReason }: { adIds: number[]; status: string; rejectReason?: string }) =>
      opsApi.bulkWantedStatus(adIds, status, rejectReason),
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['roomWanted'] });
      qc.invalidateQueries({ queryKey: ['ops', 'stats'] });
      toast.success(`Updated ${data?.affected ?? 0} ads`);
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Bulk update failed'),
  });
}

export function useBulkWantedDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (adIds: number[]) => opsApi.bulkWantedDelete(adIds),
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['roomWanted'] });
      qc.invalidateQueries({ queryKey: ['ops', 'stats'] });
      toast.success(`Deleted ${data?.affected ?? 0} ads`);
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Bulk delete failed'),
  });
}

export function useBulkWantedFlags() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ adIds, flags }: { adIds: number[]; flags: Record<string, boolean | undefined> }) =>
      opsApi.bulkWantedFlags(adIds, flags),
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['roomWanted'] });
      toast.success(`Updated ${data?.affected ?? 0} ads`);
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Bulk flags update failed'),
  });
}
